import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Siren, CheckCircle, Clock, ArrowClockwise, ArrowCounterClockwise, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

export default function VoucherComplaintsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [expanded, setExpanded] = useState(null);  // complaint id
  const [showResolve, setShowResolve] = useState(false);
  const [showReopen, setShowReopen] = useState(false);
  const [active, setActive] = useState(null);
  const [resolveForm, setResolveForm] = useState({ in_favor_of: 'vendor', resolution_notes: '' });
  const [reopenReason, setReopenReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      const r = await api.get(`/voucher-stock/complaints?${params.toString()}`);
      setItems(r.data || []);
    } catch (err) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openResolve = (c) => {
    setActive(c);
    setResolveForm({ in_favor_of: 'vendor', resolution_notes: '' });
    setShowResolve(true);
  };
  const doResolve = async () => {
    try {
      await api.post(`/voucher-stock/complaints/${active.id}/resolve`, resolveForm);
      toast.success(`Resolved in favor of ${resolveForm.in_favor_of}`);
      setShowResolve(false);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const openReopen = (c) => { setActive(c); setReopenReason(''); setShowReopen(true); };
  const doReopen = async () => {
    if (reopenReason.trim().length < 3) { toast.error('Reason dalo'); return; }
    try {
      await api.post(`/voucher-stock/complaints/${active.id}/reopen`, { reason: reopenReason });
      toast.success('Reopened');
      setShowReopen(false);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const statusColors = {
    open: 'bg-amber-100 text-amber-700',
    under_review: 'bg-orange-100 text-orange-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    reopened: 'bg-red-100 text-red-700',
    closed: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="space-y-5" data-testid="voucher-complaints-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Siren size={26} weight="duotone" className="text-red-600" /> Voucher Complaints
        </h1>
        <div className="ml-auto flex gap-2 flex-wrap">
          <div className="flex gap-1">
            {['', 'open', 'under_review', 'resolved', 'reopened'].map((s) => (
              <button key={s || 'all'} onClick={() => setFilterStatus(s)}
                className={`px-3 py-1 text-xs font-semibold rounded border ${filterStatus === s ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-gray-300'}`}
                data-testid={`filter-${s || 'all'}`}>
                {s ? s.replace('_', ' ').toUpperCase() : 'ALL'}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load}><ArrowClockwise size={14} /> Refresh</Button>
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        : items.length === 0 ? <div className="p-8 text-center text-sm text-gray-500">No complaints</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Ticket</th>
                  <th className="px-3 py-2 text-left">Voucher</th>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-left">Assigned Staff</th>
                  <th className="px-3 py-2 text-left">Raised By</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-right">Credit Held</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <>
                    <tr key={c.id} className="border-t hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                      data-testid={`complaint-row-${c.id}`}>
                      <td className="px-3 py-2 font-mono text-xs font-bold text-indigo-700">
                        <CaretRight size={10} className={`inline mr-1 transition-transform ${expanded === c.id ? 'rotate-90' : ''}`} />
                        {c.ticket_no || '—'}
                        {c.reopen_count > 0 && <span className="ml-1 text-[9px] bg-red-100 text-red-700 px-1 rounded">R{c.reopen_count}</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{c.voucher_code}</td>
                      <td className="px-3 py-2 text-xs">{c.vendor_name}</td>
                      <td className="px-3 py-2 text-xs">{c.assigned_to_staff_name || '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-semibold">{c.raised_by_name}</div>
                        <div className="text-[10px] text-gray-500">{c.raised_by_type}</div>
                      </td>
                      <td className="px-3 py-2"><span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-red-100 text-red-700">{c.complaint_type?.toUpperCase()}</span></td>
                      <td className="px-3 py-2 text-right font-semibold text-red-700">{fmt(c.credited_amount)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${statusColors[c.status] || 'bg-gray-100'}`}>
                          {c.status?.replace('_', ' ').toUpperCase()}
                        </span>
                        {c.in_favor_of && <div className="text-[10px] text-gray-500 mt-0.5">In favor: {c.in_favor_of}</div>}
                      </td>
                      <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        {(c.status === 'open' || c.status === 'under_review' || c.status === 'reopened') && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 gap-1" onClick={() => openResolve(c)} data-testid={`resolve-${c.id}`}>
                            <CheckCircle size={12} /> Resolve
                          </Button>
                        )}
                        {c.status === 'resolved' && (
                          <Button size="sm" variant="outline" className="text-amber-600 h-7 gap-1" onClick={() => openReopen(c)} data-testid={`reopen-${c.id}`}>
                            <ArrowCounterClockwise size={12} /> Reopen
                          </Button>
                        )}
                      </td>
                    </tr>
                    {expanded === c.id && (
                      <tr key={`${c.id}-timeline`} className="border-t bg-indigo-50/40">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs font-semibold text-gray-700 mb-2">Complaint Details</div>
                              <div className="text-xs space-y-1">
                                <div><span className="text-gray-500">Details:</span> {c.details || '—'}</div>
                                {c.vendor_counter_query && (
                                  <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                                    <div className="text-[11px] font-semibold text-orange-700">Vendor Counter-Query:</div>
                                    <div className="text-xs text-orange-900 mt-0.5">{c.vendor_counter_query}</div>
                                    <div className="text-[10px] text-orange-600 mt-1">{fmtDate(c.vendor_counter_query_at)}</div>
                                  </div>
                                )}
                                {c.resolution_notes && (
                                  <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
                                    <div className="text-[11px] font-semibold text-emerald-700">Resolution (in favor of {c.in_favor_of}):</div>
                                    <div className="text-xs text-emerald-900 mt-0.5">{c.resolution_notes}</div>
                                    <div className="text-[10px] text-emerald-600 mt-1">By {c.resolved_by_name} • {fmtDate(c.resolved_at)}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-gray-700 mb-2">Timeline ({c.timeline?.length || 0} events)</div>
                              <div className="space-y-1 max-h-48 overflow-y-auto">
                                {c.timeline?.map((t, i) => (
                                  <div key={i} className="text-[11px] flex gap-2 items-start">
                                    <Clock size={10} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <div><span className="font-semibold text-indigo-700">{t.action}</span> — {t.by_name} <span className="text-gray-400">({t.by_role})</span></div>
                                      {t.notes && <div className="text-gray-500 text-[10px]">{t.notes}</div>}
                                      <div className="text-gray-400 text-[9px]">{fmtDate(t.at)}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resolve Dialog */}
      <Dialog open={showResolve} onOpenChange={setShowResolve}>
        <DialogContent>
          <DialogHeader><DialogTitle>Resolve Complaint — Ticket {active?.ticket_no}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-xs bg-gray-50 p-2 rounded">
                <div><span className="text-gray-500">Voucher:</span> {active.voucher_code} • {fmt(active.voucher_amount)}</div>
                <div><span className="text-gray-500">Staff:</span> {active.assigned_to_staff_name || '—'}</div>
                <div><span className="text-gray-500">Credit held:</span> <span className="text-red-700 font-semibold">{fmt(active.credited_amount)}</span></div>
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1">Decision — Kiska paksh sahi hai?</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'staff', label: 'Staff Right', color: 'blue', desc: 'Vendor stays debited, staff credit back' },
                    { key: 'vendor', label: 'Vendor Right', color: 'emerald', desc: 'Vendor credit back, staff stays debited' },
                    { key: 'neither', label: 'Neutral', color: 'gray', desc: 'Both credit back (revert all)' },
                  ].map((o) => (
                    <button key={o.key} type="button"
                      onClick={() => setResolveForm({ ...resolveForm, in_favor_of: o.key })}
                      className={`p-2 border rounded text-xs text-left ${resolveForm.in_favor_of === o.key ? `bg-${o.color}-50 border-${o.color}-400 text-${o.color}-800` : 'border-gray-300'}`}
                      data-testid={`favor-${o.key}`}>
                      <div className="font-semibold">{o.label}</div>
                      <div className="text-[10px] text-gray-600 mt-0.5">{o.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold">Resolution Notes</label>
                <textarea rows={3} className="w-full px-3 py-2 border rounded text-sm"
                  value={resolveForm.resolution_notes}
                  onChange={(e) => setResolveForm({ ...resolveForm, resolution_notes: e.target.value })}
                  placeholder="Admin ne kaise decide kiya... (evidence, logs, etc.)" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolve(false)}>Cancel</Button>
            <Button onClick={doResolve} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-resolve-btn">
              Confirm Resolution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Dialog */}
      <Dialog open={showReopen} onOpenChange={setShowReopen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reopen Ticket {active?.ticket_no}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
              Reopen karne se previous resolution ka ledger adjustment reverse ho jayega aur ticket wapas open state me aayega.
            </div>
            <label className="text-xs font-semibold">Reopen Reason *</label>
            <textarea rows={3} className="w-full px-3 py-2 border rounded text-sm"
              value={reopenReason} onChange={(e) => setReopenReason(e.target.value)}
              placeholder="Naya evidence ya reason..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReopen(false)}>Cancel</Button>
            <Button onClick={doReopen} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-reopen-btn">
              Reopen Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
